import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDisplayName } from '../utils/displayName';

interface LineupPlayer {
  id: number;
  name: string;
  display_name?: string;
  tennisrecord_name?: string | null;
  email?: string;
}

interface LineupPosition {
  id: number;
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  player1: LineupPlayer | null;
  player2?: LineupPlayer | null;
  opponent_player1_name?: string | null;
  opponent_player2_name?: string | null;
}

interface LineupFormatPosition {
  position_number: number;
  match_type: string;
}

interface LineupFormat {
  id: number;
  positions: LineupFormatPosition[];
  max_sets_per_line?: number;
}

interface TeamMember {
  id: number;
  user_id: number;
  user?: { name: string; display_name?: string; tennisrecord_name?: string | null; ntrp_rating?: number | null };
}

interface Team {
  id: number;
  name: string;
  league?: string | null;
  section?: string | null;
  district_area?: string | null;
  flight_subflight?: string | null;
  lineup_format?: LineupFormat | null;
  members?: TeamMember[];
}

interface Match {
  id: number;
  team_id: number;
  opponent_name: string;
  match_date: string;
  location?: string | null;
  home_away?: string | null;
  team?: Team | null;
}

interface OpponentPlayer {
  id: number;
  name: string;
  ntrp_rating?: number | null;
}

interface OpponentTeamStats {
  opponent_name: string;
  players: OpponentPlayer[];
}

interface CompetitiveIntelligence {
  opponent_teams: OpponentTeamStats[];
}

function formatMatchType(matchType: string): string {
  if (!matchType) return '';
  let formatted = matchType.replace(/_/g, ' ');
  formatted = formatted.replace(/\bmens\b/gi, "men's");
  formatted = formatted.replace(/\bwomens\b/gi, "women's");
  return formatted
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default function MatchScorecardPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const [match, setMatch] = useState<Match | null>(null);
  const [lineup, setLineup] = useState<LineupPosition[]>([]);
  const [competitiveIntel, setCompetitiveIntel] = useState<CompetitiveIntelligence | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!matchId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const matchNum = parseInt(matchId, 10);
        if (Number.isNaN(matchNum)) {
          setError('Invalid match ID');
          return;
        }

        const [matchRes, lineupRes] = await Promise.all([
          axios.get<Match>(`/matches/${matchId}`),
          axios.get<LineupPosition[]>(`/lineups/matches/${matchId}/lineup`),
        ]);

        const matchData = matchRes.data;
        setMatch(matchData);
        const lineupData = Array.isArray(lineupRes.data) ? lineupRes.data : [];
        setLineup(lineupData);

        if (!matchData.team_id) {
          setLoading(false);
          return;
        }

        const teamId = matchData.team_id;
        const members = matchData.team?.members;
        if (members && members.length > 0) {
          setTeamMembers(members);
        } else {
          try {
            const teamRes = await axios.get<{ members: TeamMember[] }>(`/teams/${teamId}`);
            setTeamMembers(teamRes.data.members || []);
          } catch {
            setTeamMembers([]);
          }
        }

        try {
          const intelRes = await axios.get<CompetitiveIntelligence>(
            `/teams/${teamId}/competitive-intelligence`
          );
          setCompetitiveIntel(intelRes.data);
        } catch {
          setCompetitiveIntel(null);
        }
      } catch (err: unknown) {
        const message =
          axios.isAxiosError(err) && err.response?.data?.detail
            ? String(err.response.data.detail)
            : 'Failed to load score card data.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [matchId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5dc]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5dc] p-4">
        <div className="text-center text-gray-800">
          <p className="text-lg font-medium">{error || 'Match not found.'}</p>
        </div>
      </div>
    );
  }

  const team = match.team;
  const lineupFormat = team?.lineup_format;
  const positions = lineupFormat?.positions ?? [];
  const maxSets = Math.min(5, Math.max(1, lineupFormat?.max_sets_per_line ?? 1));
  const isOurTeamHome = match.home_away === 'home';
  const homeTeamName = isOurTeamHome ? team?.name ?? 'Our Team' : match.opponent_name;
  const visitingTeamName = isOurTeamHome ? match.opponent_name : team?.name ?? 'Our Team';

  const matchDate = match.match_date ? new Date(match.match_date) : null;
  const dateScheduledStr = matchDate
    ? `${matchDate.getMonth() + 1}/${matchDate.getDate()}/${matchDate.getFullYear()} ${matchDate.getHours() % 12 || 12}:${String(matchDate.getMinutes()).padStart(2, '0')} ${matchDate.getHours() < 12 ? 'AM' : 'PM'}`
    : 'TBD';
  const timeStr = matchDate
    ? `${matchDate.getHours() % 12 || 12}:${String(matchDate.getMinutes()).padStart(2, '0')} ${matchDate.getHours() < 12 ? 'am' : 'pm'}`
    : '';

  const opponentRoster =
    competitiveIntel?.opponent_teams?.find(
      (ot) => ot.opponent_name?.toLowerCase() === match.opponent_name?.toLowerCase()
    )?.players ?? [];

  const positionMatchTypes = new Map<number, string>();
  positions.forEach((p) => positionMatchTypes.set(p.position_number, p.match_type));

  const matchTypeCounts = new Map<string, number>();
  lineup.forEach((pos) => {
    const mt = positionMatchTypes.get(pos.position_number) ?? '';
    matchTypeCounts.set(mt, (matchTypeCounts.get(mt) || 0) + 1);
  });
  const matchTypeIndices = new Map<string, number>();

  const getPositionLabel = (positionNumber: number): string => {
    const matchType = positionMatchTypes.get(positionNumber) ?? '';
    const formatted = formatMatchType(matchType);
    const count = matchTypeCounts.get(matchType) ?? 1;
    const idx = (matchTypeIndices.get(matchType) ?? 0) + 1;
    matchTypeIndices.set(matchType, idx);
    return count > 1 ? `${formatted} #${idx}` : formatted;
  };

  const getOurPlayerNames = (pos: LineupPosition): string => {
    const p1 = pos.player1 ? getDisplayName(pos.player1) : '';
    if (pos.player2) return `${p1} & ${getDisplayName(pos.player2)}`;
    return p1;
  };

  const getOpponentPlayerNames = (pos: LineupPosition): string => {
    const o1 = pos.opponent_player1_name?.trim();
    const o2 = pos.opponent_player2_name?.trim();
    if (o1 && o2) return `${o1} & ${o2}`;
    if (o1) return o1;
    return '';
  };

  const homeMembers = isOurTeamHome ? teamMembers : opponentRoster;
  const visitingMembers = isOurTeamHome ? opponentRoster : teamMembers;
  const homeMembersLabel = isOurTeamHome ? team?.name : match.opponent_name;
  const visitingMembersLabel = isOurTeamHome ? match.opponent_name : team?.name;

  const formatNtrp = (rating: number | string | null | undefined): string | null => {
    if (rating == null) return null;
    const n = typeof rating === 'number' ? rating : parseFloat(String(rating));
    if (Number.isNaN(n)) return null;
    return n.toFixed(1);
  };

  const formatMemberDisplay = (
    m: TeamMember | OpponentPlayer,
    index: number
  ): string => {
    const name =
      'user' in m
        ? ((m as TeamMember).user ? getDisplayName((m as TeamMember).user!) : 'Unknown')
        : (m as OpponentPlayer).name;
    const rating =
      'user' in m
        ? (m as TeamMember).user?.ntrp_rating
        : (m as OpponentPlayer).ntrp_rating;
    const num = index + 1;
    const formattedRating = formatNtrp(rating);
    return formattedRating != null ? `(${num}) ${name} [${formattedRating}]` : `(${num}) ${name}`;
  };

  matchTypeIndices.clear();

  const tdStyle: React.CSSProperties = {
    fontFamily: 'Arial, Helvetica, sans-serif',
    verticalAlign: 'middle',
    textAlign: 'left',
  };

  return (
    <div
      className="min-h-screen print:bg-white"
      style={{ backgroundColor: 'beige' }}
    >
      <style>{`
        .scorecard-container table { margin: 0; border-collapse: collapse; }
        .scorecard-container td { font-family: Arial, Helvetica, sans-serif; vertical-align: middle; text-align: left; }
        .scorecard-container .scorecard-font-plus1 { font-size: 1.2em; }
        .scorecard-container .scorecard-font-minus1 { font-size: 0.875em; }
        .scorecard-container .scorecard-main-table table td { padding: 0; border: none; }
        .scorecard-container .scorecard-main-table table td.scorecard-vs-cell { padding-left: 12px; padding-right: 12px; }
        .scorecard-container .scorecard-main-table table td.scorecard-score-cell { padding: 0 6px 0 10px; vertical-align: top; }
        .scorecard-container .scorecard-outer-td { padding: 0 12px; vertical-align: top; }
        .scorecard-container .scorecard-header-td { padding: 1px 20px; }
        .scorecard-container .scorecard-header-value-td { padding: 0 20px 1px; line-height: 1.1; }
        .scorecard-container .scorecard-row-35 { height: 28px; min-height: 28px; line-height: 1.2; }
        .scorecard-container .scorecard-position-block { padding-top: 14px; padding-bottom: 14px; }
        .scorecard-container .scorecard-name-row-td { padding: 2px 0; }
        .scorecard-container .scorecard-score-stack { display: flex; flex-direction: column; gap: 4px; }
        .scorecard-container .scorecard-score-line { display: flex; align-items: center; gap: 4px; }
        .scorecard-container .scorecard-date-row-td { padding-top: 8px; }
        .scorecard-container .scorecard-spacer-row-td { height: 12px; line-height: 0; padding: 0; }
        .scorecard-container .scorecard-match-block-td { padding: 10px 12px; }
        .scorecard-container .scorecard-header-spacer-td { padding: 4px 20px; line-height: 0; }
        .scorecard-container input[type="text"] {
          font-family: Arial, Helvetica, sans-serif;
          font-size: inherit;
          border: 1px solid #999;
          background: #fff;
          padding: 1px 2px;
          margin: 0;
          box-sizing: border-box;
          width: auto;
          line-height: 1.2;
        }
        .scorecard-container input[type="text"][size="2"] { width: 2em; text-align: center; min-width: 2em; }
        .scorecard-container input[type="text"][size="7"] { width: 5em; min-width: 5em; }
        .scorecard-container input[type="text"][size="25"] { width: 16em; max-width: 100%; }
        .scorecard-container input[type="text"][size="27"] { width: 18em; max-width: 100%; }
        .scorecard-container input[type="radio"] { margin: 0 2px 0 0; vertical-align: middle; padding: 0; }
        .scorecard-container input:disabled { background: #f5f5f5; color: #333; }
        .scorecard-container .scorecard-vs-cell { padding-left: 12px; padding-right: 12px; }
        .scorecard-container .scorecard-score-cell { padding-left: 10px; padding-right: 6px; }
        .scorecard-container .scorecard-score-hyphen { padding-left: 4px; padding-right: 4px; }
        .scorecard-container .scorecard-footer-table td { padding: 4px 10px; }
        @media print {
          body { background: white !important; }
          .scorecard-no-print { display: none !important; }
          .scorecard-break-avoid { break-inside: avoid; }
        }
      `}</style>
      <div className="scorecard-no-print" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Link
          to={`/matches/${match.id}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            background: '#e5e7eb',
            color: '#1f2937',
            border: 'none',
            borderRadius: 4,
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to match
        </Link>
        <button
          type="button"
          onClick={handlePrint}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            background: '#1f2937',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 500,
          }}
        >
          <Printer className="w-4 h-4" />
          Print score card
        </button>
      </div>

      <div className="scorecard-container" style={{ width: 806, margin: '0 auto', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 16 }}>
        {/* Header table - no border, tablewidth 806px; minimal padding like template */}
        <table style={{ width: 806, borderCollapse: 'collapse', marginBottom: 0 }}>
          <tbody>
            <tr>
              <td className="scorecard-header-td" style={{ ...tdStyle, fontWeight: 'bold' }}><strong>Flight</strong></td>
              <td className="scorecard-header-td" style={{ ...tdStyle, fontWeight: 'bold' }}><strong>USTA Section</strong></td>
              <td className="scorecard-header-td" style={{ ...tdStyle, fontWeight: 'bold' }}><strong>District/Area</strong></td>
              <td className="scorecard-header-td" style={{ ...tdStyle, fontWeight: 'bold' }}><strong>League</strong></td>
            </tr>
            <tr>
              <td className="scorecard-header-td scorecard-header-value-td" style={tdStyle}>{team?.flight_subflight ?? '\u2014'}</td>
              <td className="scorecard-header-td scorecard-header-value-td" style={tdStyle}>{team?.section ?? '\u2014'}</td>
              <td className="scorecard-header-td scorecard-header-value-td" style={tdStyle}>{team?.district_area ?? '\u2014'}</td>
              <td className="scorecard-header-td scorecard-header-value-td" style={tdStyle}>{team?.league ?? team?.name ?? '\u2014'}</td>
            </tr>
            <tr>
              <td className="scorecard-header-td scorecard-header-spacer-td" style={tdStyle} colSpan={4}>&nbsp;</td>
            </tr>
          </tbody>
        </table>

        {/* Main scorecard table - border 1px solid black, 806px, cellspacing/cellpadding 0 */}
        <table
          className="scorecard-main-table"
          cellSpacing={0}
          cellPadding={0}
          style={{ width: 806, border: '1px solid black', borderCollapse: 'collapse', fontSize: 16 }}
        >
          <tbody>
            {/* Match block row */}
            <tr>
              <td className="scorecard-outer-td scorecard-match-block-td">
                <table width="100%" cellSpacing={0} cellPadding={0} style={{ backgroundColor: 'Beige' }}>
                  <tbody>
                    <tr>
                      <td colSpan={2} style={{ width: '19%', ...tdStyle }}>
                        <span className="scorecard-font-plus1"><b>Match #{match.id}</b></span>
                      </td>
                      <td colSpan={3} align="left" valign="top" style={{ width: '24%', ...tdStyle }}>
                        <span className="scorecard-font-plus1">{homeTeamName}</span>
                        <br />
                        <span className="scorecard-font-minus1">Team ID:&nbsp;*****</span>
                      </td>
                      <td className="scorecard-vs-cell" colSpan={3} align="center" valign="top" style={{ width: '16%', ...tdStyle }}>Vs.</td>
                      <td colSpan={4} align="center" valign="top" style={{ width: '28%', ...tdStyle }}>
                        <span className="scorecard-font-plus1">{visitingTeamName}</span>
                        <br />
                        <span className="scorecard-font-minus1">Team ID:&nbsp;*****</span>
                      </td>
                      <td colSpan={3} style={{ width: '13%', ...tdStyle }}>&nbsp;</td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={tdStyle} />
                      <td colSpan={4} style={tdStyle}>
                        <span className="scorecard-font-minus1">Date Scheduled:&nbsp;<strong>{dateScheduledStr}</strong></span>
                      </td>
                      <td colSpan={3} style={tdStyle}><span className="scorecard-font-minus1">&nbsp;</span></td>
                      <td colSpan={5} style={tdStyle}><span className="scorecard-font-minus1">&nbsp;</span></td>
                      <td colSpan={1} style={tdStyle} />
                    </tr>
                    <tr>
                      <td colSpan={2} style={tdStyle}>
                        <span className="scorecard-font-minus1"><strong>Match Win Criteria:&nbsp;</strong></span>
                      </td>
                      <td colSpan={14} style={tdStyle}>
                        <span className="scorecard-font-minus1">Total Games</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="scorecard-row-35" valign="top" style={{ width: '12%', ...tdStyle }}>&nbsp;</td>
                      <td valign="top" style={{ width: '7%', ...tdStyle }}>&nbsp;</td>
                      <td colSpan={3} align="left" style={{ width: '24%', ...tdStyle }}>
                        <span className="scorecard-font-minus1"><b>Home Team</b></span>
                      </td>
                      <td align="center" style={{ width: '4%', ...tdStyle }}>&nbsp;</td>
                      <td valign="top" style={{ width: '5%', ...tdStyle }}>&nbsp;</td>
                      <td valign="top" style={{ width: '5%', ...tdStyle }}>&nbsp;</td>
                      <td colSpan={3} align="left" style={{ width: '24%', ...tdStyle }}>
                        <span className="scorecard-font-minus1"><b>Visiting Team</b></span>
                      </td>
                      <td valign="top" style={{ width: '8%', ...tdStyle }}>&nbsp;</td>
                      <td align="left" style={{ width: '5%', ...tdStyle }}>
                        <span className="scorecard-font-minus1"><b>Score</b></span>
                      </td>
                      <td align="center" style={{ width: '1%', ...tdStyle }}>&nbsp;</td>
                      <td align="center" style={{ width: '1%', ...tdStyle }}>&nbsp;</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* Position rows - each a tr with one td containing inner table; use real inputs/radios like template */}
            {lineup
              .slice()
              .sort((a, b) => a.position_number - b.position_number)
              .map((pos) => {
                const label = getPositionLabel(pos.position_number);
                const ourNames = getOurPlayerNames(pos);
                const oppNames = getOpponentPlayerNames(pos);
                const homeNames = isOurTeamHome ? ourNames : oppNames;
                const visitingNames = isOurTeamHome ? oppNames : ourNames;
                const homeParts = homeNames ? homeNames.split(/\s*&\s*/) : ['', ''];
                const visitingParts = visitingNames ? visitingNames.split(/\s*&\s*/) : ['', ''];
                const homePlayer1 = homeParts[0] ?? '';
                const homePlayer2 = homeParts[1] ?? '';
                const visitingPlayer1 = visitingParts[0] ?? '';
                const visitingPlayer2 = visitingParts[1] ?? '';
                const winnerName = `strWinner_${pos.id}`;

                return (
                  <tr key={pos.id} className="scorecard-break-avoid">
                    <td className="scorecard-outer-td scorecard-position-block">
                          <table width="100%" cellSpacing={0} cellPadding={0} style={{ backgroundColor: 'Beige' }}>
                            <tbody>
                              <tr>
                                <td className="scorecard-name-row-td scorecard-row-35" valign="top" style={{ width: '12%', ...tdStyle }}>
                                  <span className="scorecard-font-minus1">{label}<br />{timeStr}</span>
                                </td>
                                <td className="scorecard-name-row-td" align="right" valign="top" style={{ width: '7%', ...tdStyle }}>
                                  <span className="scorecard-font-minus1">Name:</span>
                                </td>
                                <td className="scorecard-name-row-td" colSpan={3} style={{ width: '28%', ...tdStyle }}>
                                  <span className="scorecard-font-minus1">
                                    <input readOnly type="text" size={27} value={homePlayer1} aria-label="Home player 1" />
                                  </span>
                                </td>
                                <td className="scorecard-name-row-td scorecard-vs-cell" align="center" valign="top" style={{ width: '5%', ...tdStyle }}>Vs.</td>
                                <td className="scorecard-name-row-td" align="right" valign="top" style={{ width: '7%', ...tdStyle }}>
                                  <span className="scorecard-font-minus1">Name:</span>
                                </td>
                                <td className="scorecard-name-row-td" colSpan={3} style={{ width: '28%', ...tdStyle }}>
                                  <span className="scorecard-font-minus1">
                                    <input readOnly type="text" size={25} value={visitingPlayer1} aria-label="Visiting player 1" />
                                  </span>
                                </td>
                                <td
                                  className="scorecard-name-row-td scorecard-score-cell"
                                  rowSpan={pos.player2 ? 2 : 1}
                                  valign="top"
                                  style={{ width: '13%', ...tdStyle }}
                                >
                                  <div className="scorecard-score-stack">
                                    {Array.from({ length: maxSets }).map((_, setIndex) => (
                                      <div key={setIndex} className="scorecard-score-line">
                                        <span className="scorecard-font-minus1">
                                          <input readOnly type="text" size={2} maxLength={2} value="" aria-label={`Set ${setIndex + 1} score first`} />
                                        </span>
                                        <span className="scorecard-font-minus1">-</span>
                                        <span className="scorecard-font-minus1">
                                          <input readOnly type="text" size={2} maxLength={2} value="" aria-label={`Set ${setIndex + 1} score second`} />
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                              {pos.player2 && (
                                <tr>
                                  <td className="scorecard-name-row-td" valign="top" style={{ width: '12%', ...tdStyle }} />
                                  <td className="scorecard-name-row-td" align="right" valign="top" style={{ width: '7%', ...tdStyle }}>
                                    <span className="scorecard-font-minus1">Name:</span>
                                  </td>
                                  <td className="scorecard-name-row-td" colSpan={3} style={{ width: '28%', ...tdStyle }}>
                                    <span className="scorecard-font-minus1">
                                      <input readOnly type="text" size={27} value={homePlayer2} aria-label="Home player 2" />
                                    </span>
                                  </td>
                                  <td className="scorecard-name-row-td" align="center" valign="top" style={{ width: '5%', ...tdStyle }} />
                                  <td className="scorecard-name-row-td" align="right" valign="top" style={{ width: '7%', ...tdStyle }}>
                                    <span className="scorecard-font-minus1">Name:</span>
                                  </td>
                                  <td className="scorecard-name-row-td" colSpan={3} style={{ width: '28%', ...tdStyle }}>
                                    <span className="scorecard-font-minus1">
                                      <input readOnly type="text" size={25} value={visitingPlayer2} aria-label="Visiting player 2" />
                                    </span>
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td className="scorecard-date-row-td" style={tdStyle}>&nbsp;</td>
                                <td align="right" valign="top" style={tdStyle}>
                                  <span className="scorecard-font-minus1">Date:</span>
                                </td>
                                <td style={tdStyle}>
                                  <span className="scorecard-font-minus1">
                                    <input readOnly type="text" size={7} name="strMatchDate" disabled value="" aria-label="Date" />
                                  </span>
                                </td>
                                <td align="right" valign="top" style={tdStyle}>
                                  <span className="scorecard-font-minus1">Winner</span>
                                </td>
                                <td valign="top" style={tdStyle}>
                                  <span className="scorecard-font-minus1">
                                    <input readOnly type="radio" name={winnerName} value="Home" disabled aria-label="Winner Home" />
                                  </span>
                                </td>
                                <td style={tdStyle}>&nbsp;</td>
                                <td valign="top" align="right" style={tdStyle}>
                                  <span className="scorecard-font-minus1">Winner</span>
                                </td>
                                <td valign="top" style={tdStyle}>
                                  <span className="scorecard-font-minus1">
                                    <input readOnly type="radio" name={winnerName} value="Visitor" disabled aria-label="Winner Visitor" />
                                  </span>
                                </td>
                                <td valign="top" align="right" style={tdStyle}>
                                  <span className="scorecard-font-minus1">Status:</span>
                                </td>
                                <td align="right" style={tdStyle}>
                                  <span className="scorecard-font-minus1">
                                    <input readOnly type="text" size={7} value="" aria-label="Status" />
                                  </span>
                                </td>
                                <td className="scorecard-score-cell" style={{ width: '13%', ...tdStyle }}>&nbsp;</td>
                              </tr>
                              <tr>
                                <td colSpan={10} className="scorecard-spacer-row-td" style={tdStyle}>&nbsp;</td>
                                <td className="scorecard-spacer-row-td" style={tdStyle}>&nbsp;</td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* Footer: Members in Home Team - border solid, border-top none, cellpadding 10px */}
        <table
          className="scorecard-footer-table"
          cellSpacing={0}
          cellPadding={10}
          style={{
            width: 806,
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: 'black',
            borderTop: 'none',
            borderCollapse: 'collapse',
            ...tdStyle,
          }}
        >
          <tbody>
            <tr>
              <td style={tdStyle}>
                <b>{homeMembersLabel}</b>&nbsp;<span className="scorecard-font-minus1">Members in Home Team:&nbsp;</span>
              </td>
            </tr>
            <tr>
              <td style={tdStyle}>
                {homeMembers.length > 0 ? (
                  <table style={{ width: 650 }}>
                    <tbody>
                      {Array.from({ length: Math.ceil(homeMembers.length / 4) }).map((_, rowIdx) => (
                        <tr key={rowIdx}>
                          {[0, 1, 2, 3].map((colIdx) => {
                            const idx = rowIdx * 4 + colIdx;
                            const member = homeMembers[idx];
                            return (
                              <td key={colIdx} style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                <span className="scorecard-font-minus1">
                                  {member ? formatMemberDisplay(member as TeamMember | OpponentPlayer, idx) : '\u00A0'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  '\u2014'
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Footer: Members in Visiting Team - cellpadding 10px */}
        <table
          className="scorecard-footer-table"
          cellSpacing={0}
          cellPadding={10}
          style={{
            width: 806,
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: 'black',
            borderTop: 'none',
            borderCollapse: 'collapse',
            ...tdStyle,
          }}
        >
          <tbody>
            <tr>
              <td style={tdStyle}>
                <b>{visitingMembersLabel}</b>&nbsp;<span className="scorecard-font-minus1">Members in Visiting Team:&nbsp;</span>
              </td>
            </tr>
            <tr>
              <td style={tdStyle}>
                {visitingMembers.length > 0 ? (
                  <table style={{ width: 650 }}>
                    <tbody>
                      {Array.from({ length: Math.ceil(visitingMembers.length / 4) }).map((_, rowIdx) => (
                        <tr key={rowIdx}>
                          {[0, 1, 2, 3].map((colIdx) => {
                            const idx = rowIdx * 4 + colIdx;
                            const member = visitingMembers[idx];
                            return (
                              <td key={colIdx} style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                <span className="scorecard-font-minus1">
                                  {member ? formatMemberDisplay(member as TeamMember | OpponentPlayer, idx) : '\u00A0'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  '\u2014'
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
